import { api, LocalStorageKeys } from '@/app'
import { Hurdle } from '@/types'
import { TypeGuards } from '@codeleap/common'
import { queryClient } from '../queryClient'
import { globalState } from '@codeleap/store'
import { isLoggedIn } from '@/features/Auth/session/utils'
import { useEffect } from '@codeleap/common'
import { format } from 'date-fns'
import { PaginationResponse, QueryManager } from '@/packages'
import { UseQueryOptions } from '@tanstack/react-query'

const BASE_URL = 'games/hurdle/'

type HurdleFilters = {
  after?: Date | string
  before?: Date | string
  limit?: number
}

export const loggedOutHurdle = globalState(
  {
    id: null
  },
  {
    persistKey: LocalStorageKeys.LOGGED_OUT_HURDLE
  }
)

export function useLoggedOutHurdle(hurdleId?: number) {
  const loggedOutId = loggedOutHurdle.use(s => s.id)

  const loggedIn = isLoggedIn()
  return loggedIn ? hurdleId : loggedOutId
}

export function useSaveLoggedOutHurdle(hurdleId?: number) {
  const loggedOutId = loggedOutHurdle.use(s => s.id)
  const loggedIn = isLoggedIn()

  useEffect(() => {
    if (!loggedIn && !loggedOutId && !!hurdleId) {
      loggedOutHurdle.set({
        id: hurdleId
      })
    }
  }, [hurdleId, loggedIn, loggedOutId])

  return loggedIn ? hurdleId : loggedOutId
}

export const hurdleManager = new QueryManager({
  itemType: {} as any,
  name: 'hurdle',
  queryClient: queryClient.client,

  async listItems(limit, offset, args) {
    const response = await api.get<PaginationResponse<Hurdle>>(BASE_URL, {
      params: {
        ...args,
        limit: args?.limit ?? limit,
        offset
      },
      injectAnonymousUser: true
    })

    return response.data
  },
  async retrieveItem(id) {
    const response = await api.get<Hurdle>(BASE_URL + `${id ?? 'current'}/`, { injectAnonymousUser: true })

    return response.data
  },

  actions: {
    guess: async (manager, params: { guess: string; hurdleId: number | string }) => {
      try {
        const response = await api.post<Hurdle | { error: string }>(
          BASE_URL + `${params?.hurdleId ?? 'current'}/guess/`,
          { guess: params?.guess },
          { injectAnonymousUser: true }
        )

        return response.data
      } catch (e) {
        return e?.data || e
      }
    }
  }
})

const formatParam = (date: Date | string) => {
  if (TypeGuards.isString(date)) return date
  return format(date, 'yyyy-MM-dd')
}

export function useHurdles(filters?: HurdleFilters) {
  filters.before = filters.before ? formatParam(filters.before) : filters.before
  filters.after = filters.after ? formatParam(filters.after) : filters.before

  const manager = hurdleManager.use({
    filter: filters,
    limit: filters?.limit ?? 20
  })

  return {
    manager,
    items: manager.items,
    loading: manager.list.query.isLoading
  }
}

export const useHurdle = (id?: number | string, options?: UseQueryOptions<Hurdle>) => {
  const item = hurdleManager.useItem({
    id: id ?? 'current',
    queryOptions: options
  })

  return item.query
}
